import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { ArrowRight, Mail, Phone } from 'lucide-react';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Button } from '@/shared/ui/button';
import { useAuthStore } from '@/features/auth/store';
import { useToast } from '@/shared/hooks/use-toast';
import { userApi } from '@/features/user/api';
import { AuthLayout } from '../layouts/AuthLayout';
import { AuthFormLayout } from '../layouts/AuthFormLayout';
import { PasswordInput } from '../components/PasswordInput';
import { LoadingButton } from '../components/LoadingButton';


const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const Login = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/app';
  const { toast } = useToast();
  const login = useAuthStore((s) => s.login);
  const loginWithGoogle = useAuthStore((s) => s.loginWithGoogle);

  const [mode, setMode] = useState<'email' | 'phone'>('email');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);

  const switchMode = (next: 'email' | 'phone') => {
    setMode(next);
    setPassword('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      let emailToUse: string;

      if (mode === 'phone') {
        const fullPhone = phone.trim();
        const resolved = await userApi.resolveEmailByPhone(fullPhone);
        if (!resolved) {
          toast({ title: t('common.error'), description: t('auth.login.phoneNotFound'), variant: 'destructive' });
          setIsLoading(false);
          return;
        }
        emailToUse = resolved;
      } else {
        emailToUse = email.trim();
      }

      await login(emailToUse, password);
      navigate(from, { replace: true });
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code ?? '';
      const description =
        code === 'auth/invalid-credential' || code === 'auth/wrong-password' || code === 'auth/user-not-found'
          ? t('auth.login.invalidCredentials')
          : code === 'auth/too-many-requests'
          ? t('auth.login.tooManyAttempts')
          : t('auth.login.invalidCredentials');
      toast({ title: t('common.error'), description, variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogle = async () => {
    setIsGoogleLoading(true);
    try {
      const { needsProfileCompletion } = await loginWithGoogle();
      navigate(needsProfileCompletion ? '/complete-profile' : from, { replace: true });
    } catch (err: unknown) {
      if ((err as { code?: string })?.code !== 'auth/popup-closed-by-user') {
        toast({ title: t('common.error'), description: t('auth.login.googleSignInFailed'), variant: 'destructive' });
      }
    } finally {
      setIsGoogleLoading(false);
    }
  };

  return (
    <AuthLayout
      branding={{
        title: t('auth.login.brandingTitle'),
        subtitle: t('auth.login.brandingSubtitle'),
      }}
    >
      <AuthFormLayout
        title={t('auth.login.title')}
        subtitle={t('auth.login.subtitle')}
      >
        {/* Google sign-in — not available on native */}
        {!Capacitor.isNativePlatform() && (
          <Button
            type="button"
            variant="outline"
            className="w-full h-12 gap-3 font-medium"
            onClick={handleGoogle}
            disabled={isGoogleLoading || isLoading}
          >
            {isGoogleLoading ? (
              <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
            ) : (
              <GoogleIcon />
            )}
            {t('auth.login.continueWithGoogle')}
          </Button>
        )}

        <div className="relative my-5">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-border" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">{t('auth.login.or')}</span>
          </div>
        </div>

        {/* Email / Phone tab toggle */}
        <div className="flex rounded-lg border border-border p-1 gap-1 bg-muted/30">
          <button
            type="button"
            onClick={() => switchMode('email')}
            className={`flex-1 flex items-center justify-center gap-2 rounded-md py-2.5 text-sm font-medium transition-colors ${
              mode === 'email'
                ? 'bg-background shadow-sm text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Mail className="w-4 h-4" />
            {t('common.email')}
          </button>
          <button
            type="button"
            onClick={() => switchMode('phone')}
            className={`flex-1 flex items-center justify-center gap-2 rounded-md py-2.5 text-sm font-medium transition-colors ${
              mode === 'phone'
                ? 'bg-background shadow-sm text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Phone className="w-4 h-4" />
            {t('auth.login.mobile')}
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5 mt-5">
          {mode === 'email' ? (
            <div className="space-y-2">
              <Label htmlFor="login-email">{t('common.email')}</Label>
              <Input
                id="login-email"
                type="email"
                placeholder={t('auth.login.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12 input-elegant"
                autoComplete="email"
                required
                autoFocus
              />
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="login-phone">{t('auth.login.mobileNumber')}</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                <Input
                  id="login-phone"
                  type="tel"
                  placeholder="+49 176 1234 5678"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="h-12 pl-9 input-elegant"
                  autoComplete="tel"
                  required
                  autoFocus
                />
              </div>
            </div>
          )}

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="login-password">{t('common.password')}</Label>
              <Link to="/forgot-password" className="text-sm text-accent hover:underline">
                {t('auth.login.forgotPassword')}
              </Link>
            </div>
            <PasswordInput
              id="login-password"
              label=""
              value={password}
              onChange={setPassword}
              required
            />
          </div>

          <LoadingButton
            type="submit"
            isLoading={isLoading}
            loadingText={t('auth.login.signingIn')}
            icon={ArrowRight}
          >
            {t('auth.login.submit')}
          </LoadingButton>
        </form>

        <p className="text-center text-sm text-muted-foreground mt-8">
          {t('auth.login.noAccount')}{' '}
          <Link to="/register" className="text-accent font-medium hover:underline">
            {t('auth.login.registerLink')}
          </Link>
        </p>
      </AuthFormLayout>
    </AuthLayout>
  );
};

export default Login;
